<template>
  <div class="mb-8">
    <SurveyScopeControlsSection
      :state="state"
      :actions="actions"
      @update:surveySeasonKey="$emit('update:surveySeasonKey', $event)"
    />

    <div class="mb-8 border rounded-lg bg-white p-4">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 class="text-lg font-semibold">
          Survey response analytics
          <span class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.survey_response" role="img" aria-label="Info">ⓘ</span>
        </h3>
        <div class="flex items-center gap-2">
          <label class="text-xs text-gray-600">View</label>
          <select v-model="surveyCeremonyViewModeProxy" class="border rounded px-2 py-1 text-xs">
            <option value="table">Table</option>
            <option value="chart">Chart</option>
          </select>
          <label class="text-xs text-gray-600 ml-2">Sort</label>
          <select v-model="surveyCeremonySortModeProxy" class="border rounded px-2 py-1 text-xs">
            <option value="date_asc">Date (oldest first)</option>
            <option value="date_desc">Date (newest first)</option>
            <option value="response_rate_desc">Performance: Response rate</option>
            <option value="responders_desc">Performance: Responders</option>
            <option value="registrations_desc">Performance: Registrations</option>
          </select>
          <template v-if="state.surveyCeremonyViewMode === 'chart'">
            <label class="text-xs text-gray-600 ml-2">Metric</label>
            <select v-model="surveyCeremonyChartMetricProxy" class="border rounded px-2 py-1 text-xs">
              <option value="response_rate_pct">Response rate %</option>
              <option value="responders">Responders</option>
              <option value="total_registrations">Registrations</option>
            </select>
          </template>
        </div>
      </div>

      <div v-if="state.surveyLoading" class="text-sm text-gray-500">Loading survey analytics...</div>
      <div v-else-if="state.surveyError" class="text-sm text-red-600">{{ state.surveyError }}</div>
      <div v-else-if="state.surveyStats">
        <div class="mb-4 flex flex-wrap items-center gap-2">
          <button @click="actions.setSurveyAnalysisTab('overview')" :class="['px-3 py-2 rounded border text-xs', state.surveyAnalysisTab === 'overview' ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']">Overview</button>
          <button @click="actions.setSurveyAnalysisTab('questions')" :class="['px-3 py-2 rounded border text-xs', state.surveyAnalysisTab === 'questions' ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']">Questions</button>
          <button @click="actions.setSurveyAnalysisTab('ceremony')" :class="['px-3 py-2 rounded border text-xs', state.surveyAnalysisTab === 'ceremony' ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']">Ceremony</button>
          <button @click="actions.setSurveyAnalysisTab('compare')" :class="['px-3 py-2 rounded border text-xs', state.surveyAnalysisTab === 'compare' ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']">Compare</button>
          <button
            @click="actions.setSurveyAnalysisTab('insights')"
            :class="['px-3 py-2 rounded border text-xs inline-flex items-center gap-1.5', state.surveyAnalysisTab === 'insights' ? 'bg-purple-200 text-purple-900 border-purple-300' : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100']"
          >
            <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.539 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
            </svg>
            <span>Insights</span>
          </button>
        </div>

        <SurveyOverviewSection v-if="state.surveyAnalysisTab === 'overview'" :survey-stats="state.surveyStats" :tooltips="tooltips" />

        <SurveyQuestionsSection
          v-else-if="state.surveyAnalysisTab === 'questions'"
          :state="state"
          :actions="actions"
          :tooltips="tooltips"
          @update:surveyQuestionSortMode="$emit('update:surveyQuestionSortMode', $event)"
          @update:surveyQuestionCeremonyViewMode="$emit('update:surveyQuestionCeremonyViewMode', $event)"
          @update:surveyQuestionCeremonySortMode="$emit('update:surveyQuestionCeremonySortMode', $event)"
          @update:surveyQuestionCeremonyChartMetric="$emit('update:surveyQuestionCeremonyChartMetric', $event)"
        />

        <SurveyCeremonySection
          v-else-if="state.surveyAnalysisTab === 'ceremony'"
          :state="state"
          :actions="actions"
          @update:surveyCeremonyViewMode="$emit('update:surveyCeremonyViewMode', $event)"
          @update:surveyCeremonySortMode="$emit('update:surveyCeremonySortMode', $event)"
          @update:surveyCeremonyChartMetric="$emit('update:surveyCeremonyChartMetric', $event)"
        />

        <SurveyCompareSection
          v-else-if="state.surveyAnalysisTab === 'compare'"
          :state="state"
          :actions="actions"
          @update:surveyCompareMode="$emit('update:surveyCompareMode', $event)"
          @update:surveyCompareMetric="$emit('update:surveyCompareMetric', $event)"
          @update:surveyCompareSortMode="$emit('update:surveyCompareSortMode', $event)"
          @update:surveyComparisonRowsCollapsed="$emit('update:surveyComparisonRowsCollapsed', $event)"
          @update:surveyCompareQuestionKey="$emit('update:surveyCompareQuestionKey', $event)"
          @update:surveyQuestionCompareMetric="$emit('update:surveyQuestionCompareMetric', $event)"
          @update:surveyQuestionCompareSortMode="$emit('update:surveyQuestionCompareSortMode', $event)"
          @update:surveyQuestionComparisonRowsCollapsed="$emit('update:surveyQuestionComparisonRowsCollapsed', $event)"
          @update:surveyComparisonChartLimit="$emit('update:surveyComparisonChartLimit', $event)"
          @update:surveyComparisonChartOrientation="$emit('update:surveyComparisonChartOrientation', $event)"
          @update:surveyQuestionComparisonChartLimit="$emit('update:surveyQuestionComparisonChartLimit', $event)"
          @update:surveyQuestionComparisonChartOrientation="$emit('update:surveyQuestionComparisonChartOrientation', $event)"
          @update:surveyOptionChartLimit="$emit('update:surveyOptionChartLimit', $event)"
          @update:surveyOptionChartOrientation="$emit('update:surveyOptionChartOrientation', $event)"
        />

        <SurveyInsightsSection
          v-else-if="state.surveyAnalysisTab === 'insights'"
          :state="state"
          :actions="actions"
          @update:surveyInsightsSegmentBy="$emit('update:surveyInsightsSegmentBy', $event)"
        />

        <div v-else class="text-sm text-gray-500">Select a survey view.</div>
      </div>

      <div v-else class="text-sm text-gray-500">No survey data available for this scope.</div>
    </div>

    <SurveyReportsSection
      v-if="!state.isAllScope && state.surveyStats"
      :state="state"
      :actions="actions"
      @update:surveyReportPack="$emit('update:surveyReportPack', $event)"
    />

    <SurveyExportSection
      v-if="state.canExportSurvey && !state.isAllScope && state.surveyStats"
      :state="state"
      :actions="actions"
      @update:surveyExportMode="$emit('update:surveyExportMode', $event)"
      @update:surveyExportFieldPreset="$emit('update:surveyExportFieldPreset', $event)"
      @update:surveyExportQuestionKey="$emit('update:surveyExportQuestionKey', $event)"
      @update:surveyExportPermissionQuestionKey="$emit('update:surveyExportPermissionQuestionKey', $event)"
      @update:surveyExportContactableOnly="$emit('update:surveyExportContactableOnly', $event)"
    />
  </div>
</template>

<script>
import SurveyCeremonySection from './SurveyCeremonySection.vue'
import SurveyCompareSection from './SurveyCompareSection.vue'
import SurveyExportSection from './SurveyExportSection.vue'
import SurveyInsightsSection from './SurveyInsightsSection.vue'
import SurveyOverviewSection from './SurveyOverviewSection.vue'
import SurveyQuestionsSection from './SurveyQuestionsSection.vue'
import SurveyReportsSection from './SurveyReportsSection.vue'
import SurveyScopeControlsSection from './SurveyScopeControlsSection.vue'

export default {
  name: 'SurveyPanel',
  components: {
    SurveyCeremonySection,
    SurveyCompareSection,
    SurveyExportSection,
    SurveyInsightsSection,
    SurveyOverviewSection,
    SurveyQuestionsSection,
    SurveyReportsSection,
    SurveyScopeControlsSection,
  },
  props: {
    state: { type: Object, required: true },
    actions: { type: Object, required: true },
    tooltips: { type: Object, default: () => ({}) },
  },
  computed: {
    surveyCeremonyViewModeProxy: {
      get() { return this.state.surveyCeremonyViewMode },
      set(value) { this.$emit('update:surveyCeremonyViewMode', value) },
    },
    surveyCeremonySortModeProxy: {
      get() { return this.state.surveyCeremonySortMode },
      set(value) { this.$emit('update:surveyCeremonySortMode', value) },
    },
    surveyCeremonyChartMetricProxy: {
      get() { return this.state.surveyCeremonyChartMetric },
      set(value) { this.$emit('update:surveyCeremonyChartMetric', value) },
    },
  },
}
</script>
